/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => ({
  include_contact_inboxes: false,
  page,
  sort: sortAttr,
  ...(search ? { q: search } : {}),
  ...(label ? { labels: [label] } : {}),
});

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    return axios.get(this.url, {
      params: buildContactParams(page, sortAttr, label, ''),
    });
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId, { inboxId, conversationId } = {}) {
    const params = {};
    if (inboxId) params.inbox_id = inboxId;
    if (conversationId) params.conversation_id = conversationId;
    return axios.get(`${this.url}/${contactId}/conversations`, { params });
  }

  getAttachments(contactId, page = 1) {
    return axios.get(`${this.url}/${contactId}/attachments`, {
      params: { page },
    });
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  initiateCall(contactId, inboxId, conversationId = null) {
    return axios.post(`${this.url}/${contactId}/call`, {
      inbox_id: inboxId,
      conversation_id: conversationId,
    });
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '', options = {}) {
    return axios.get(`${this.url}/search`, {
      params: buildContactParams(page, sortAttr, label, search),
      signal: options.signal,
    });
  }

  active(page = 1, sortAttr = 'name') {
    return axios.get(`${this.url}/active`, {
      params: buildContactParams(page, sortAttr),
    });
  }

  commercial(
    page = 1,
    sortAttr = '-last_activity_at',
    status = 'todos',
    period = 'todos'
  ) {
    return axios.get(`${this.url}/commercial`, {
      params: {
        ...buildContactParams(page, sortAttr),
        status,
        period,
      },
    });
  }

  recovery(
    page = 1,
    period = 'todos',
    type = 'todos',
    sort = 'date_asc'
  ) {
    return axios.get(`${this.url}/recovery`, {
      params: {
        ...buildContactParams(page, 'name'),
        period,
        type,
        sort,
      },
    });
  }

  overview() {
    return axios.get(`${this.url}/overview`);
  }

  addToCommercial(contactId) {
    return axios.post(`${this.url}/${contactId}/add_to_commercial`);
  }

  startRenewal(contactId) {
    return axios.post(`${this.url}/${contactId}/start_renewal`);
  }

  closeCommercial(contactId, nextRecoveryOn) {
    return axios.post(`${this.url}/${contactId}/close_commercial`, {
      next_recovery_on: nextRecoveryOn,
    });
  }

  closures(page = 1, month = '', sort = 'date_desc', perPage = null) {
    return axios.get(`${this.url}/closures`, {
      params: {
        ...buildContactParams(page, 'name'),
        month,
        sort,
        ...(perPage ? { per_page: perPage } : {}),
      },
    });
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    return axios.post(`${this.url}/filter`, queryPayload, {
      params: buildContactParams(page, sortAttr),
    });
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }
}

export default new ContactAPI();
